import { useMemo, useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";

// Components
import Author from "components/Table/Author";
import MDBox from "components/MDBox";
import Status from "components/Table/Status";
import By from "components/Table/By";

// Material Design
import { IconButton } from "@mui/material";

import DprExportMenu from "layouts/dpr/dprExportMenu";
import { exportDprPdfStreamed, exportDprExcel } from "redux/Thunks/Dpr";

// Utilities
import Constants, { Icons, defaultData } from "utils/Constants";
import { getFormattedProjectName } from "utils/methods/methods";

function DprData({
  dprList,
  handleOpenDprDetailDrawer,
  handleOpenDprDelete,
  handlePdfExportFromHOC,
  handleExcelExportFromHOC,
}) {
  const [exportDprId, setExportDprId] = useState(null);
  const exportInFlightRef = useRef(false);
  const ConfigData = useSelector((state) => state.config);
  const permission = ConfigData?.screens?.[12]?.screensInfo?.agreement;

  // Only one DPR export runs at a time; the ref also guards against double clicks before the
  // state update has re-rendered the buttons as disabled.
  const runDprExport = useCallback(async (item, runExport) => {
    if (exportInFlightRef.current) return;
    exportInFlightRef.current = true;
    setExportDprId(item[Constants.MONGOOSE_ID]);
    try {
      await runExport();
    } finally {
      exportInFlightRef.current = false;
      setExportDprId(null);
    }
  }, []);

  const handleExportDprData = useCallback(
    (item) =>
      runDprExport(item, () =>
        handlePdfExportFromHOC(
          "DPR.pdf",
          exportDprPdfStreamed({
            dprId: item[Constants.MONGOOSE_ID],
            dprVersion: item?.version,
          }),
          false
        )
      ),
    [handlePdfExportFromHOC, runDprExport]
  );

  const handleExportDprExcelData = useCallback(
    (item) =>
      runDprExport(item, () =>
        handleExcelExportFromHOC(
          `DPR-${item?.dprNo ?? ""}.xlsx`,
          exportDprExcel({
            dprId: item[Constants.MONGOOSE_ID],
            dprVersion: item?.version,
          })
        )
      ),
    [handleExcelExportFromHOC, runDprExport]
  );

  const rows = useMemo(() => {
    if (!dprList || dprList.length === 0) return [];

    return dprList.map((item, index) => ({
      srNo: <Author name={index + 1} />,
      dprNo: <Author name={item.dprNo} />,
      project: <Author name={getFormattedProjectName(item?.project)} />,
      version: (
        <Author name={item?.prevVersion || item?.version} style={{ textTransform: "initial" }} />
      ),
      date: (
        <Author
          name={
            item?.dprDate ? moment(item?.dprDate).utc().format(defaultData.WEB_DATE_FORMAT) : "---"
          }
        />
      ),
      status: <Status title={item?.status && item?.status?.replace("_", " ")} />,
      createdBy: (
        <By
          name={`${
            (item?.createdBy?.callingName
              ? item?.createdBy?.callingName
              : item?.createdBy?.firstName) ?? ""
          } ${item?.createdBy?.lastName ?? ""}`}
          isSuperAdmin={item?.createdBy?.role?.[0]?.title === defaultData.SUPER_ADMIN_ROLE}
        />
      ),
      createdAt: (
        <Author
          name={
            item?.createdAt ? moment(item?.createdAt).format(defaultData.WEB_DATE_FORMAT) : "---"
          }
        />
      ),
      action: (
        <MDBox>
          {permission?.read && (
            <IconButton
              aria-label="View DPR"
              color="info"
              onClick={() => handleOpenDprDetailDrawer(item?.[Constants.MONGOOSE_ID])}
            >
              {Icons.VIEW}
            </IconButton>
          )}
          {permission?.update && (
            <Link
              to={`/client/dpr/${item?.[Constants.MONGOOSE_ID]}?projectId=${
                item?.project?.[Constants.MONGOOSE_ID]
              }`}
              state={{
                dprId: item?.[Constants.MONGOOSE_ID],
                projectId: item?.project?.[Constants.MONGOOSE_ID],
                projectStatus: item?.status,
              }}
            >
              <IconButton aria-label="Edit DPR" color="info">
                {Icons.EDIT}
              </IconButton>
            </Link>
          )}
          {permission?.delete && (
            <IconButton
              aria-label="Delete DPR"
              color="info"
              onClick={() => handleOpenDprDelete(item?.[Constants.MONGOOSE_ID])}
            >
              {Icons.DELETE}
            </IconButton>
          )}
          <DprExportMenu
            dprId={item[Constants.MONGOOSE_ID]}
            isExporting={exportDprId === item[Constants.MONGOOSE_ID]}
            disabled={Boolean(exportDprId)}
            onDownloadPdf={() => handleExportDprData(item)}
            onDownloadExcel={() => handleExportDprExcelData(item)}
          />
        </MDBox>
      ),
    }));
  }, [
    dprList,
    permission,
    exportDprId,
    handleOpenDprDetailDrawer,
    handleOpenDprDelete,
    handleExportDprData,
    handleExportDprExcelData,
  ]);

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "srNo", width: "2%" },
      { Header: "DPR", accessor: "dprNo", width: "2%" },
      { Header: "Project", accessor: "project", align: "left", width: "25%" },
      { Header: "Version", accessor: "version", align: "left", width: "5%" },
      { Header: "Date", accessor: "date", align: "left", width: "10%" },
      { Header: "Status", accessor: "status", align: "left", width: "10%" },
      { Header: "Created By", accessor: "createdBy", align: "left", width: "15%" },
      { Header: "Created At", accessor: "createdAt", align: "left", width: "10%" },
      { Header: "Action", accessor: "action", width: "10%", align: "left" },
    ],
    []
  );

  return {
    columns,
    rows,
  };
}

export default DprData;
